import { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import { SubscriptionInfo } from '@/interfaces/user';

export const PAYMENT_NOTIFICATION_WINDOW_DAYS = 7;
export const PAYMENT_NOTIFICATION_CRITICAL_DAYS = 3;

export type SubscriptionNotificationTier = 'reminder' | 'critical' | 'expired';

export interface SubscriptionNotificationState {
  showNotification: boolean;
  endDayDiff: number;
  isExpired: boolean;
  tier: SubscriptionNotificationTier | null;
}

const HIDDEN_NOTIFICATION: SubscriptionNotificationState = {
  showNotification: false,
  endDayDiff: 0,
  isExpired: false,
  tier: null,
};

// Blocking date = duePaymentDate + gracePeriod (defaults to 0). Shows the countdown within
// PAYMENT_NOTIFICATION_WINDOW_DAYS before that date (reminder, then critical under
// PAYMENT_NOTIFICATION_CRITICAL_DAYS), and switches to `expired` once it's passed.
export const getSubscriptionNotificationState = (
  duePaymentDate: string | null | undefined,
  subscriptionInfo: Pick<SubscriptionInfo, 'gracePeriod'> | null | undefined,
  referenceDate: Moment = moment()
): SubscriptionNotificationState => {
  if (!duePaymentDate) return HIDDEN_NOTIFICATION;

  const gracePeriod = subscriptionInfo?.gracePeriod ?? 0;
  const blockedDate = moment(duePaymentDate).startOf('day').add(gracePeriod, 'days');
  const today = referenceDate.clone().startOf('day');
  const daysUntilBlocked = blockedDate.diff(today, 'days');

  if (daysUntilBlocked < 0) return { showNotification: true, endDayDiff: 0, isExpired: true, tier: 'expired' };
  if (daysUntilBlocked > PAYMENT_NOTIFICATION_WINDOW_DAYS) return HIDDEN_NOTIFICATION;

  const tier: SubscriptionNotificationTier = daysUntilBlocked <= PAYMENT_NOTIFICATION_CRITICAL_DAYS ? 'critical' : 'reminder';
  return { showNotification: true, endDayDiff: daysUntilBlocked, isExpired: false, tier };
};

export const useSubscriptionNotification = (
  duePaymentDate: string | null | undefined,
  subscriptionInfo: Pick<SubscriptionInfo, 'gracePeriod'> | null | undefined
): SubscriptionNotificationState => {
  const [state, setState] = useState<SubscriptionNotificationState>(HIDDEN_NOTIFICATION);

  useEffect(() => {
    setState(getSubscriptionNotificationState(duePaymentDate, subscriptionInfo));
  }, [duePaymentDate, subscriptionInfo]);

  return state;
};
